<template>
  <content-dialog
    :visible.sync="modalVisible"
    max-width="800px"
  >
    <!--<editor-fold desc="Dialog Title">-->
    <template slot="dialogTitle">
      <span v-if="yachtDirty.id">
        Edit Yacht
      </span>
    </template>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog Content">-->
    <v-card-text class="pa-6">
      <!--<editor-fold desc="Form">-->
      <v-text-field
        v-model="yachtDirty.hull_number"
        data-vv-as="hull number"
        :error-messages="veeErrors.collect('hull_number')"
        :disabled="!canEditHullNumber"
        name="hull_number"
        label="Hull Number"
        outlined
      />
      <v-text-field
        v-model="yachtDirty.name"
        :error-messages="veeErrors.collect('name')"
        :disabled="!canEditName"
        name="name"
        label="Name"
        data-vv-as="name"
        outlined
      />
      <v-autocomplete
        v-model="yachtDirty.flag_country_id"
        :error-messages="veeErrors.collect('flag_country_id')"
        :items="countries.data"
        :loading="countries.loading"
        :disabled="!canEditFlagCountry"
        label="Flag (Registration) Country"
        item-text="name"
        item-value="id"
        data-vv-as="flag (registration) country"
        data-vv-name="flag_country_id"
        autocomplete="off"
        outlined
      />
      <v-text-field
        v-model="yachtDirty.registration_port"
        data-vv-as="port of registration"
        :error-messages="veeErrors.collect('registration_port')"
        name="registration_port"
        label="Port Of Registration"
        outlined
      />
      <v-text-field
        v-model="yachtDirty.factory_id"
        data-vv-as="factory id"
        :error-messages="veeErrors.collect('factory_id')"
        :disabled="!canEditFactoryId"
        name="factory_id"
        label="Factory ID"
        outlined
      />
      <!--</editor-fold>-->

      <!--<editor-fold desc="Logistics">-->
      <div v-if="$can('set-logistics-plan', 'Yacht')">
        <v-subheader class="mt-4">
          Logistics
        </v-subheader>
        <v-switch
          v-if="!yachtDirty.id"
          v-model="yachtDirty.is_already_ordered"
          label="Yacht is already ordered"
        />
        <v-autocomplete
          v-model="yachtDirty.destination_base_id"
          :error-messages="veeErrors.collect('destination_base_id')"
          :items="basesFilteredByCountry"
          :disabled="!canEditDestinationBase || !yachtDirty.country_id"
          name="destination_base_id"
          label="Destination Base"
          item-text="name"
          item-value="id"
          outlined
          data-vv-as="destination base"
        />
        <!--        <v-select-->
        <!--          v-model="yachtDirty.delivery_year"-->
        <!--          :error-messages="veeErrors.collect('delivery_year')"-->
        <!--          :items="deliveryYearSelectItems"-->
        <!--          :disabled="!canEditDeliveryYear"-->
        <!--          name="delivery_year"-->
        <!--          label="Delivery Year"-->
        <!--          outlined-->
        <!--          data-vv-as="delivery year"-->
        <!--        />-->
        <v-dialog
          ref="datePickerVisible"
          v-model="datePickerVisible"
          :fullscreen="$vuetify.breakpoint.smAndDown"
          transition="fade-transition"
          width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              :error-messages="veeErrors.collect('ex_factory_date')"
              :disabled="!canEditExFactoryDate"
              :value="yachtDirty.ex_factory_date ? $moment(yachtDirty.ex_factory_date).format('DD.MM.YYYY.') : ''"
              hide-details
              name="ex_factory_date"
              label="Ex Factory Date"
              readonly
              outlined
              clearable
              data-vv-as="ex factory date"
              @click:clear="yachtDirty.ex_factory_date = null"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="yachtDirty.ex_factory_date"
            no-title
            full-width
            scrollable
            @input="datePickerVisible = false"
          />
        </v-dialog>

        <v-row
          dense
          class="mt-5"
        >
          <v-col>
            <v-menu
              v-model="phaseInDatePicker"
              :close-on-content-click="false"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="yachtDirty.phase_in_date ? $moment(yachtDirty.phase_in_date).format('DD.MM.YYYY.') : ''"
                  hide-details
                  label="Phase In Date"
                  readonly
                  outlined
                  clearable
                  hint="Placeholder for agreements"
                  @click:clear="yachtDirty.phase_in_date = null"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="yachtDirty.phase_in_date"
                @input="phaseInDatePicker = false"
              />
            </v-menu>
          </v-col>
          <v-col>
            <v-menu
              v-model="phaseOutDatePicker"
              :close-on-content-click="false"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="yachtDirty.phase_out_date ? $moment(yachtDirty.phase_out_date).format('DD.MM.YYYY.') : ''"
                  hide-details
                  label="Phase Out Date"
                  readonly
                  outlined
                  clearable
                  hint="Placeholder for agreements"
                  @click:clear="yachtDirty.phase_out_date = null"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="yachtDirty.phase_out_date"
                @input="phaseOutDatePicker = false"
              />
            </v-menu>
          </v-col>
        </v-row>
      </div>
      <!--</editor-fold>-->

      <!--<editor-fold desc="Investor">-->
      <v-row
        no-gutters
        align="center"
        class="mt-4"
      >
        <v-col>
          <v-subheader>
            Yacht owner
          </v-subheader>
        </v-col>
        <v-col
          v-if="canEditOwner"
          class="shrink"
        >
          <v-btn
            color="secondary"
            text
            @click="ownerDialogVisible = true"
          >
            <v-icon left>
              add
            </v-icon>
            Add New
          </v-btn>
        </v-col>
      </v-row>

      <v-autocomplete
        v-model="yachtDirty.owner_id"
        :items="owners.data"
        :loading="owners.loading"
        :disabled="!canEditOwner"
        label="Select yacht owner"
        item-text="name"
        item-value="id"
        autocomplete="off"
        clearable
        outlined
      />
      <!--</editor-fold>-->
    </v-card-text>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog Actions">-->
    <template slot="dialogActions">
      <v-spacer />
      <v-btn
        text
        color="grey"
        @click="modalVisible = false"
      >
        Cancel
      </v-btn>
      <v-btn
        color="primary"
        :loading="submitting"
        @click="submit"
      >
        <v-icon
          small
          left
        >
          check
        </v-icon>
        Save
      </v-btn>
    </template>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialogs">-->
    <owner-edit-dialog
      :visible.sync="ownerDialogVisible"
      @owner:created="ownerCreated"
    />
    <!--</editor-fold>-->
  </content-dialog>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import Yacht from '@/models/Yacht'
import ContentDialog from '@/components/helpers/Dialog'
import OwnerEditDialog from '@/components/business/owners/OwnerEditDialog'
import OwnersApi from '@/services/api/Owners'
import { mapGetters } from 'vuex'

export default {
    components: {
      ContentDialog,
      OwnerEditDialog
    },
    props: {
      visible: Boolean,
      yacht: {
        type: Object,
        default: () => Yacht.emptyModel
      }
    },
    data() {
      return {
        yachtDirty: cloneDeep(this.yacht),
        submitting: false,
        owners: {
          loading: false,
          data: []
        },
        ownerDialogVisible: false,
        currentYear: new Date().getFullYear(),
        datePickerVisible: false,
        phaseInDatePicker: false,
        phaseOutDatePicker: false
      }
    },
    computed: {
      modalVisible: {
        get: function () {
          return this.visible
        },
        set: function (value) {
          this.$emit('update:visible', value)
        }
      },
      canEditOwner() {
        return this.yachtDirty.editable_fields.indexOf('owner_id') > -1
      },
      canEditFlagCountry() {
        return this.yachtDirty.editable_fields.indexOf('flag_country_id') > -1
      },
      canEditName() {
        return this.yachtDirty.editable_fields.indexOf('name') > -1
      },
      canEditHullNumber() {
        return this.yachtDirty.editable_fields.indexOf('hull_number') > -1
      },
      canEditDeliveryYear() {
        return this.yachtDirty.editable_fields.indexOf('delivery_year') > -1
      },
      canEditDestinationBase() {
        return this.yachtDirty.editable_fields.indexOf('destination_base_id') > -1
      },
      canEditExFactoryDate() {
        return this.yachtDirty.editable_fields.indexOf('ex_factory_date') > -1
      },
      canEditFactoryId() {
        return this.yachtDirty.editable_fields.indexOf('factory_id') > -1
      },
      basesFilteredByCountry() {
        if (!this.yachtDirty.country_id) return []

        return this.$store.getters['business/filterBasesByCountryId'](this.yachtDirty.country_id)
      },
      deliveryYearSelectItems() {
        let items = []
        for (let i = 0; i < 20; i++) { items.push(this.currentYear + i) }
        return items
      },
      ...mapGetters('settings', ['charteringCountries', 'countries'])
    },
    watch: {
      yacht: function () {
        this.yachtDirty = cloneDeep(this.yacht)
      }
    },
    created() {
      this.fetchOwners()
    },
    methods: {
      async submit() {
        let validation = await this.$validator.validate()

        if (!validation) return

        this.saveYacht()
      },
      async saveYacht() {
        this.submitting = true

        let formData = {
          destination_base_id: this.yachtDirty.destination_base_id,
          ex_factory_date: this.yachtDirty.ex_factory_date ? this.$moment(this.yachtDirty.ex_factory_date).format('YYYY-MM-DD') : null,
          phase_in_date: this.yachtDirty.phase_in_date ? this.$moment(this.yachtDirty.phase_in_date).format('YYYY-MM-DD') : null,
          phase_out_date: this.yachtDirty.phase_out_date ? this.$moment(this.yachtDirty.phase_out_date).format('YYYY-MM-DD') : null,
          delivery_year: this.yachtDirty.delivery_year,
          flag_country_id: this.yachtDirty.flag_country_id,
          registration_port: this.yachtDirty.registration_port,
          owner_id: this.yachtDirty.owner_id,
          hull_number: this.yachtDirty.hull_number,
          factory_id: this.yachtDirty.factory_id,
          name: this.yachtDirty.name,
          is_already_ordered: !this.yachtDirty.id ? this.yachtDirty.is_already_ordered : undefined
        }

        try {
          let savedYacht = await Yacht.api.save(formData, this.yachtDirty.id)

          this.$store.dispatch('alert', {
            'text': 'Yacht Saved',
            'color': 'success'
          })
          this.$emit('update:yacht', savedYacht)
          this.modalVisible = false
        } catch (e) {
          if (e.response && e.response.status === 422) {
            this.$store.dispatch('alert', {
              'text': e.response.data.message,
              'color': 'warning'
            })
          } else {
            this.$store.dispatch('alert', {
              'text': 'Something went wrong',
              'color': 'error'
            })
          }

          throw e
        } finally {
          this.submitting = false
        }
      },
      async fetchOwners() {
        if (!this.$can('view', 'Owner')) return

        this.owners.loading = true

        try {
          this.owners.data = await OwnersApi.getOwners()
        } catch (e) {
          throw e
        } finally {
          this.owners.loading = false
        }
      },
      exchangeCurrency(amount, fromCurrency, toCurrency, year) {
        if (fromCurrency === toCurrency) return amount

        return Number((amount || 0) * this.findExchangeRate(toCurrency, fromCurrency, year))
      },
      ownerCreated(owner) {
        this.owners.data.unshift(owner)
        this.yachtDirty.owner_id = owner.id
      }
    }
  }
</script>
